import { Data } from 'react-csv/lib/core';
import { formatPercent, ratio } from '../../HatUtils';
import { HatDbContextValue } from '../../context/HatDbContext';
import { HatDbDataContextValue } from '../../context/HatDbDataContext';
import { hatActionsFriendlyNames, HatMetricActionAttributionData } from '../dashboard/HatDbSectionActionAttribution';
import { HatMetricChannelAttributionData } from '../dashboard/HatDbSectionChannelAttribution';
import { mediaFilters } from '../../HatMetrics';
import { displayOrder } from '../widgets/HatDbVisualActionAttributionTable';

/**
 * Generate an export filename for a dashboard.
 * @param hatDbContext The dashboard context.
 * @returns A filename.
 */
export function getFilenameForDashboard(hatDbContext: HatDbContextValue) {
    const hatDbCriteria = hatDbContext.hatDbCriteria;
    return [
        hatDbContext.agencyName,
        hatDbContext.advertiserName,
        'HAT Reporting',
        hatDbCriteria.currentRangeStart.format('YYYYMMDD'),
        hatDbCriteria.currentRangeEnd.format('YYYYMMDD'),
    ]
        .filter((v) => !!v)
        .join(' - ');
}

/**
 * Construct a CSV by grabbing data from the HAT dashboard data context.
 * @param hatDbContext The dashboard context.
 * @param hatDbDataContext The dashboard data content.
 * @returns CSV data as an array of arrays.
 */
export function getCsvDataForDashboard(hatDbContext: HatDbContextValue, hatDbDataContext: HatDbDataContextValue): Data {
    const hatDbCriteria = hatDbContext.hatDbCriteria;

    const rows: Data = [];

    // Agency and advertiser section
    rows.push(
        ['Agency', 'Advertiser', 'Advertiser ID'],
        [hatDbContext.agencyName, hatDbContext.advertiserName, hatDbCriteria.advertiserId]
    );

    rows.push([]);
    rows.push(['HAT Reporting Analytics - Overall Performance']);

    // Criteria - media filter
    if (hatDbCriteria.mediaFilter) {
        const mediaType = mediaFilters[hatDbCriteria.mediaFilter];
        rows.push(['Media Type', mediaType.label]);
    }

    // Criteria - current range
    rows.push([
        [
            hatDbCriteria.currentRangeStart.format('MMMM DD YYYY'),
            hatDbCriteria.currentRangeEnd.format('MMMM DD YYYY'),
        ].join(' - '),
    ]);

    // Data - summary
    rows.push([]);
    rows.push(['Total Visits', 'Attributed Visits', 'Total Actions', 'Attributed Actions']);
    rows.push([
        hatDbDataContext.getData('totalVisits', 'current'),
        hatDbDataContext.getData('attributedVisits', 'current'),
        hatDbDataContext.getData('totalActions', 'current'),
        hatDbDataContext.getData('attributedActions', 'current'),
    ]);

    // Data - channel attribution
    rows.push([]);
    rows.push(['Visits']);
    appendChannelData(rows, hatDbDataContext);

    // Data - actions attribution
    rows.push([]);
    rows.push(['Actions']);
    appendActionData(rows, hatDbDataContext);

    return rows;
}

/**
 * Add the channel attribution section to the CSV.
 */
function appendChannelData(rows: Data, hatDbDataContext: HatDbDataContextValue) {
    rows.push(['Channel', 'Visits', 'Attributed Visits', '% Attributed Visits']);

    const data = hatDbDataContext.getData<HatMetricChannelAttributionData>('channelAttribution', 'current');
    if (!data) {
        return;
    }

    for (const channel of data) {
        rows.push([
            channel.sourceCategory,
            channel.count,
            channel.adInfluencedCount,
            formatPercent(ratio(channel.adInfluencedCount, channel.count)),
        ]);
    }
    rows.push([
        'Total',
        data.total?.count,
        data.total?.adInfluencedCount,
        formatPercent(ratio(data.total?.adInfluencedCount, data.total?.count)),
    ]);
}

/**
 * Add the actions attribution section to the CSV.
 */
function appendActionData(rows: Data, hatDbDataContext: HatDbDataContextValue) {
    rows.push(['Action', 'Count', 'Attributed Actions', '% Attributed Actions']);

    const data = hatDbDataContext.getData('actionAttribution', 'current') as HatMetricActionAttributionData | null;
    if (!data) {
        return;
    }

    for (const actionName of displayOrder) {
        const action = data[actionName];

        rows.push([
            hatActionsFriendlyNames[actionName],
            action.count,
            action.adInfluencedCount,
            formatPercent(ratio(action.adInfluencedCount, action.count)),
        ]);
    }
    rows.push([
        'Total',
        data.total?.count,
        data.total?.adInfluencedCount,
        formatPercent(ratio(data.total?.adInfluencedCount, data.total?.count)),
    ]);
}
